import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Card } from "primereact/card";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { Checkbox } from "primereact/checkbox";
import { InputText } from "primereact/inputtext";
import { Toast } from "primereact/toast";
import { ProgressSpinner } from "primereact/progressspinner";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import "../../YouTubeScorecard/Components/FilterBars/auditFilters.css";
import {
  mvMetadataGapApi,
  MvMetadataGapReviewEntry,
} from "../../../api/mvMetadataGapApi";

const ALL_BRANDS = "";

const VIEW_OPTIONS = [
  { label: "Needs Brand Mapping", value: "unmapped" as const },
  { label: "Ready to Apply", value: "ready" as const },
  { label: "Completed", value: "completed" as const },
];

function applySummaryToast(summary: {
  labelsApplied: number;
  artistsApplied: number;
  skipped: number;
  reviewsQueued: number;
  errors: number;
  results?: Array<{ error?: string; episodeBrand?: string }>;
}): { severity: "success" | "warn" | "info" | "error"; summary: string; detail: string } {
  const changed = summary.labelsApplied + summary.artistsApplied;
  const detailParts = [
    `${summary.labelsApplied} label(s), ${summary.artistsApplied} artist(s) on YouTube`,
  ];
  if (summary.skipped) detailParts.push(`${summary.skipped} skipped`);
  if (summary.reviewsQueued) {
    detailParts.push(`${summary.reviewsQueued} queued (no artist resolved)`);
  }
  detailParts.push(`${summary.errors} error(s)`);

  if (summary.errors) {
    return { severity: "warn", summary: "Partial apply", detail: detailParts.join(" · ") };
  }
  if (changed > 0) {
    return { severity: "success", summary: "Applied", detail: detailParts.join(" · ") };
  }
  if (summary.reviewsQueued > 0) {
    const brandHint = summary.results?.find((r) => r.episodeBrand)?.episodeBrand;
    return {
      severity: "warn",
      summary: "No YouTube updates",
      detail: brandHint
        ? `Could not resolve artist for brand "${brandHint}" — check Brand → Artist Lookup spelling matches the row brand exactly.`
        : "Could not resolve an artist — check Brand → Artist Lookup.",
    };
  }
  if (summary.skipped > 0) {
    return {
      severity: "info",
      summary: "Already up to date",
      detail: `${summary.skipped} asset(s) already have label + artist on YouTube. The table may show stale BQ values until the next sync.`,
    };
  }
  return { severity: "info", summary: "Nothing changed", detail: detailParts.join(" · ") };
}

const MvMetadataGapReviewTab = () => {
  const toast = useRef<Toast>(null);
  const [entries, setEntries] = useState<MvMetadataGapReviewEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [viewMode, setViewMode] = useState<"ready" | "unmapped" | "completed">("ready");
  const [stats, setStats] = useState<{
    pendingReviews: number;
    unmappedBrandReviews: number;
  } | null>(null);
  const [brandFilter, setBrandFilter] = useState(ALL_BRANDS);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [{ entries: reviews }, statsData] = await Promise.all([
        mvMetadataGapApi.getReviews(
          viewMode === "ready"
            ? { readyToApplyOnly: true }
            : viewMode === "unmapped"
              ? { unmappedOnly: true }
              : { completedOnly: true }
        ),
        mvMetadataGapApi.getStats(),
      ]);
      setEntries(reviews);
      setStats(statsData);
    } catch {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to load MV metadata gap reviews",
        life: 4000,
      });
    } finally {
      setLoading(false);
    }
  }, [viewMode]);

  const brandNames = useMemo(() => {
    const brands = new Set<string>();
    for (const entry of entries) {
      const brand = entry.episodeBrand?.trim();
      if (brand) brands.add(brand);
    }
    return Array.from(brands).sort((a, b) => a.localeCompare(b));
  }, [entries]);

  const brandOptions = useMemo(
    () => [
      { label: "All Brands", value: ALL_BRANDS },
      ...brandNames.map((brand) => ({ label: brand, value: brand })),
    ],
    [brandNames]
  );

  const filteredEntries = useMemo(() => {
    const brand = typeof brandFilter === "string" ? brandFilter.trim() : "";
    if (!brand) return entries;
    const needle = brand.toLowerCase();
    return entries.filter((entry) =>
      (entry.episodeBrand ?? "").toLowerCase().includes(needle)
    );
  }, [entries, brandFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    setBrandFilter(ALL_BRANDS);
  }, [viewMode]);

  useEffect(() => {
    const brand = typeof brandFilter === "string" ? brandFilter.trim() : "";
    if (brand && !brandNames.includes(brand)) {
      setBrandFilter(ALL_BRANDS);
    }
  }, [brandFilter, brandNames]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await mvMetadataGapApi.syncFromBigQuery();
      toast.current?.show({
        severity: "success",
        summary: "Synced",
        detail: `${result.queued} queued from ${result.synced} BQ rows`,
        life: 4000,
      });
      await loadData();
    } catch {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Sync failed",
        life: 4000,
      });
    } finally {
      setSyncing(false);
    }
  };

  const handleApplyNow = () => {
    const checked = filteredEntries.filter((e) => e.checked);
    let targets =
      checked.length > 0
        ? checked
        : filteredEntries.filter((e) => e.selectedArtist);

    if (!targets.length && viewMode === "ready" && filteredEntries.length > 0) {
      targets = filteredEntries;
    }

    if (!targets.length) {
      toast.current?.show({
        severity: "warn",
        summary: "Nothing to Apply",
        detail:
          viewMode === "ready"
            ? "No assets in Ready to Apply — sync from BigQuery or map brands first."
            : "Map the brand in Brand → Artist Lookup, then switch to Ready to Apply.",
        life: 5000,
      });
      return;
    }

    confirmDialog({
      message: `Apply Label & Artist to ${targets.length} asset(s) on YouTube now?`,
      header: "Apply to YouTube",
      icon: "pi pi-exclamation-triangle",
      accept: async () => {
        setApplying(true);
        try {
          const summary = await mvMetadataGapApi.applyReviews(
            targets.map((e) => ({
              musicVideoAssetId: e.musicVideoAssetId,
              artist: e.selectedArtist,
              episodeBrand: e.episodeBrand,
            }))
          );
          const toastMsg = applySummaryToast(summary);
          toast.current?.show({
            severity: toastMsg.severity,
            summary: toastMsg.summary,
            detail: toastMsg.detail,
            life: 7000,
          });
          await loadData();
        } catch (err: unknown) {
          const detail =
            err &&
            typeof err === "object" &&
            "response" in err &&
            err.response &&
            typeof err.response === "object" &&
            "data" in err.response &&
            err.response.data &&
            typeof err.response.data === "object" &&
            "message" in err.response.data
              ? String(err.response.data.message)
              : "Apply failed";
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail,
            life: 5000,
          });
        } finally {
          setApplying(false);
        }
      },
    });
  };

  const handleApplyBrandNow = () => {
    const brand = typeof brandFilter === "string" ? brandFilter.trim() : "";
    if (!brand) return;

    confirmDialog({
      message: `Apply Label & Artist to all BigQuery gap assets for "${brand}" now? (Uses approved brand lookup when artist is not selected on a row.)`,
      header: "Apply Brand to YouTube",
      icon: "pi pi-exclamation-triangle",
      accept: async () => {
        setApplying(true);
        try {
          const summary = await mvMetadataGapApi.applyNowForBrand(brand);
          const toastMsg = applySummaryToast(summary);
          toast.current?.show({
            severity: toastMsg.severity,
            summary: toastMsg.summary,
            detail: toastMsg.detail,
            life: 7000,
          });
          await loadData();
        } catch (err: unknown) {
          const detail =
            err &&
            typeof err === "object" &&
            "response" in err &&
            err.response &&
            typeof err.response === "object" &&
            "data" in err.response &&
            err.response.data &&
            typeof err.response.data === "object" &&
            "message" in err.response.data
              ? String(err.response.data.message)
              : "Apply failed";
          toast.current?.show({
            severity: "error",
            summary: "Error",
            detail,
            life: 5000,
          });
        } finally {
          setApplying(false);
        }
      },
    });
  };

  const updateLocal = (id: string, patch: Partial<MvMetadataGapReviewEntry>) => {
    setEntries((prev) =>
      prev.map((e) => (e.id === id ? { ...e, ...patch } : e))
    );
  };

  const saveRow = async (row: MvMetadataGapReviewEntry) => {
    try {
      await mvMetadataGapApi.updateReview(row.musicVideoAssetId, {
        selectedArtist: row.selectedArtist,
        checked: row.checked,
        notes: row.notes,
      });
    } catch {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to save row",
        life: 3000,
      });
    }
  };

  const artistSelector = (row: MvMetadataGapReviewEntry) => {
    const options = (row.dropdownOptions ?? []).map((a) => ({
      label: a,
      value: a,
    }));
    return (
      <Dropdown
        value={row.selectedArtist ?? null}
        options={options}
        onChange={async (e) => {
          updateLocal(row.id, { selectedArtist: e.value });
          await mvMetadataGapApi.updateReview(row.musicVideoAssetId, {
            selectedArtist: e.value,
          });
        }}
        placeholder="Select Artist"
        filter
        showClear
        style={{ width: "100%" }}
      />
    );
  };

  const checkedBody = (row: MvMetadataGapReviewEntry) => (
    <Checkbox
      checked={row.checked ?? false}
      onChange={async (e) => {
        updateLocal(row.id, { checked: e.checked ?? false });
        await saveRow({ ...row, checked: e.checked ?? false });
      }}
    />
  );

  const notesBody = (row: MvMetadataGapReviewEntry) => (
    <InputText
      value={row.notes ?? ""}
      onBlur={async (e) => {
        updateLocal(row.id, { notes: e.target.value });
        await saveRow({ ...row, notes: e.target.value });
      }}
      onChange={(e) => updateLocal(row.id, { notes: e.target.value })}
      style={{ width: "100%" }}
    />
  );

  return (
    <>
      <Toast ref={toast} />
      <ConfirmDialog />
      <Card title="MV Metadata Gaps">
        <div className="mv-metadata-intro">
          <ul className="mv-metadata-view-guide">
            <li className="mv-metadata-view-guide-item">
              <strong>Needs Brand Mapping</strong>
              <span>
                Brands still waiting for an artist in the Brand → Artist Lookup tab.
              </span>
            </li>
            <li className="mv-metadata-view-guide-item">
              <strong>Ready to Apply</strong>
              <span>
                Brand and artist are approved — apply label + artist to YouTube from here.
              </span>
            </li>
            <li className="mv-metadata-view-guide-item">
              <strong>Completed</strong>
              <span>Label + artist already applied from this tool.</span>
            </li>
          </ul>
          <p className="mv-metadata-view-guide-note">
            Assets with no catalog brand (invalid <code>custom_id</code>) are out of scope and
            not shown.
          </p>
          {stats && (
            <div className="mv-metadata-stats-row">
              <span className="mv-metadata-stat-pill">
                Pending Reviews: {stats.pendingReviews}
              </span>
              <span className="mv-metadata-stat-pill">
                Unmapped Brand Queue: {stats.unmappedBrandReviews}
              </span>
            </div>
          )}
        </div>
        <div className="filter-container">
          <div className="mv-metadata-filters">
            <div className="filter-item">
              <span className="mv-gap-filter-label">View</span>
              <Dropdown
                inputId="mv-gap-view-mode"
                value={viewMode}
                options={VIEW_OPTIONS}
                onChange={(e) => setViewMode(e.value ?? "ready")}
                style={{ width: "100%", maxWidth: "280px", borderRadius: "5px" }}
              />
            </div>
            <div className="filter-item">
              <span className="mv-gap-filter-label">Brand</span>
              <Dropdown
                inputId="mv-gap-brand-filter"
                value={brandFilter}
                options={brandOptions}
                onChange={(e) =>
                  setBrandFilter(typeof e.value === "string" ? e.value : ALL_BRANDS)
                }
                placeholder="All Brands"
                disabled={!entries.length}
                filter
                style={{ width: "100%", maxWidth: "320px", borderRadius: "5px" }}
              />
            </div>
          </div>
          <div className="mv-metadata-toolbar">
            <Button
              label="Refresh"
              icon="pi pi-refresh"
              outlined
              severity="secondary"
              onClick={loadData}
            />
            <Button
              label="Sync from BigQuery"
              icon="pi pi-cloud-download"
              outlined
              severity="info"
              loading={syncing}
              onClick={handleSync}
            />
            {viewMode === "ready" && (
              <>
                <span className="mv-metadata-toolbar-divider" aria-hidden="true" />
                <Button
                  label="Apply Label + Artist Now"
                  icon="pi pi-bolt"
                  severity="success"
                  loading={applying}
                  onClick={handleApplyNow}
                />
                {brandFilter.trim() && (
                  <Button
                    label={`Apply All for ${brandFilter.trim()}`}
                    icon="pi pi-check-circle"
                    severity="warning"
                    loading={applying}
                    onClick={handleApplyBrandNow}
                  />
                )}
              </>
            )}
          </div>
        </div>
        {loading ? (
          <ProgressSpinner />
        ) : (
          <DataTable
            value={filteredEntries}
            paginator
            rows={25}
            rowsPerPageOptions={[25, 50, 100]}
            paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
            stripedRows
            size="small"
            emptyMessage={
              viewMode === "ready"
                ? "No assets Ready to Apply — map brands first, then sync from BigQuery."
                : viewMode === "completed"
                  ? "No completed applies yet."
                  : "No assets need Brand Mapping."
            }
          >
            <Column field="musicVideoAssetId" header="MV Asset ID" sortable />
            <Column field="customId" header="Custom ID" sortable />
            <Column field="episodeBrand" header="Brand" sortable />
            <Column field="assetTitle" header="Title" />
            <Column field="currentLabel" header="Current Label" />
            <Column field="currentArtist" header="Current Artist" />
            {viewMode !== "completed" && (
              <>
                <Column
                  header="Select Artist"
                  body={artistSelector}
                  style={{ minWidth: "200px" }}
                />
                <Column field="reviewStatus" header="Status" sortable />
                <Column header="Checked" body={checkedBody} />
                <Column header="Notes" body={notesBody} style={{ minWidth: "140px" }} />
              </>
            )}
            {viewMode === "completed" && (
              <>
                <Column field="reviewStatus" header="Status" sortable />
                <Column field="appliedBy" header="Applied By" sortable />
                <Column field="appliedAt" header="Applied At" sortable />
              </>
            )}
          </DataTable>
        )}
      </Card>
    </>
  );
};

export default MvMetadataGapReviewTab;
